import { useMemo } from 'react';
import { SelectInput } from '@interstate/components/SelectInput';
import { NumericInput } from '@makemydeal/dr-shared-components/numeric-input';
import type { InterstateOnChangeEvent } from '@interstate/components/InterstateEvents';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { useCustomerCardContext } from '../../CustomerFormContext';
import CustomerFieldsLayoutHeader from './CustomerFieldsLayoutHeader';
import { FieldsRow } from '../sectionLayout.styles';
import { DurationField } from './DurationField';
import { HOUSING_STATUS_OPTIONS } from './housingAndEmployment.utils';
import { DEFAULT_ERROR_STATE, type ErrorState } from '@makemydeal/dr-dash-ui-utils';

export const CurrentAddress = () => {
    const { formValues, formErrors, handleInputChange, handleNumberInputChange, isLocked } = useCustomerCardContext();

    const housingInfo = formValues.housingInfo;
    const housingErrors = formErrors?.housingInfo as Record<string, ErrorState> | undefined;

    const errors = useMemo(
        () => ({
            housingStatus: housingErrors?.housingStatus ?? DEFAULT_ERROR_STATE,
            mortgageOrRentAmount: housingErrors?.mortgageOrRentAmount ?? DEFAULT_ERROR_STATE,
            yearsAtCurrentAddress: (formErrors as Record<string, ErrorState>)?.yearsAtCurrentAddress ?? DEFAULT_ERROR_STATE,
            monthsAtCurrentAddress: (formErrors as Record<string, ErrorState>)?.monthsAtCurrentAddress ?? DEFAULT_ERROR_STATE
        }),
        [housingErrors, formErrors]
    );

    const handleHousingStatusChange = (value: string | undefined) => {
        handleInputChange('housingInfo.housingStatus', value ?? '');
    };

    const handleMortgageChange = (e: InterstateOnChangeEvent<string>) => {
        handleNumberInputChange('housingInfo.mortgageOrRentAmount', e.target.value);
    };

    const handleDurationChange = (field: string, value: string) => {
        handleNumberInputChange(field, value);
    };

    return (
        <div data-testid="current-address-section">
            <CustomerFieldsLayoutHeader>Current Address</CustomerFieldsLayoutHeader>
            <FieldsRow $columns={4} $mdColumns={2} $smColumns={1}>
                <SelectInput
                    name="housingStatus"
                    displayLabel
                    label="Housing Status"
                    value={housingInfo?.housingStatus ?? ''}
                    onChange={(e) => handleHousingStatusChange(e.target.value)}
                    options={HOUSING_STATUS_OPTIONS}
                    disabled={isLocked}
                    hasError={errors.housingStatus.hasError}
                    errorMessage={errors.housingStatus.errorMessage}
                    data-testid="customer-form-housing-status"
                    size="small"
                />

                <NumericInput
                    name="monthlyMortgageRent"
                    displayLabel
                    label="Monthly Mortgage/Rent"
                    value={housingInfo?.mortgageOrRentAmount}
                    onChange={handleMortgageChange}
                    allowDecimal
                    autoInsertCommas
                    decimalMaxLength={2}
                    decimalMinLength={0}
                    inputPrefix="$"
                    disabled={isLocked}
                    hasError={errors.mortgageOrRentAmount.hasError}
                    errorMessage={errors.mortgageOrRentAmount.errorMessage}
                    selectOnFocusSelector={featureToggleSelectors.isAutoSelectContentOnSlideOutsEnabledAfterBootstrap}
                    data-testid="customer-form-monthly-mortgage-rent"
                    size="small"
                />

                <DurationField
                    label="Yrs. at Address"
                    field="yearsAtCurrentAddress"
                    value={formValues.yearsAtCurrentAddress}
                    error={errors.yearsAtCurrentAddress}
                    onChange={handleDurationChange}
                    disabled={isLocked}
                />

                <DurationField
                    label="Mos. at Address"
                    field="monthsAtCurrentAddress"
                    value={formValues.monthsAtCurrentAddress}
                    error={errors.monthsAtCurrentAddress}
                    onChange={handleDurationChange}
                    disabled={isLocked}
                />
            </FieldsRow>
        </div>
    );
};

export default CurrentAddress;
